<template>
  <PageHeader>
    <template #header-title>
      <WdsStack direction-column gap-md>
        <WdsStack gap-md align-center>
          <WdsAvatar :path="avatarPath" lg />
          {{ $t('Hi, :user', { user: userName }) }}
        </WdsStack>
        <WdsStack v-if="isCompany || isRecruiter" gap-sm>
          <WdsLabel primary subtle>
            <WdsStack justify-center align-center gap-xs>
              <WdsIcon :name="accountIcon" :size="12" />
              {{ accountName }}
            </WdsStack>
          </WdsLabel>
          <WdsLabel subtle>
            <WdsStack justify-center align-center gap-xs>
              <WdsIcon name="user" :size="12" />
              {{ $t(':role role', { role: $t(roleName) }) }}
            </WdsStack>
          </WdsLabel>
        </WdsStack>
      </WdsStack>
    </template>
    <template #header-description>
      <slot name="header-alerts"></slot>
    </template>
  </PageHeader>

  <!-- Default/old dashboard (kept in DOM via v-show so Blade-mounted Vue apps survive toggling) -->
  <div v-show="!shouldShowNewDashboard">
    <slot></slot>
  </div>

  <!-- Company Dashboard -->
  <template v-if="isCompany && shouldShowNewDashboard">
    <slot name="company-supplier-sections"></slot>
    <CompanyDashboard />
  </template>

  <!-- Worker Dashboard -->
  <FreelancerDashboard v-if="shouldShowNewDashboard && isFreelancer" />

  <!-- Staffing Agency Dashboard -->
  <StaffingAgencyDashboard v-if="shouldShowNewDashboard && isRecruiter" />
</template>

<script setup lang="ts">
import {
  WdsStack,
  WdsLabel,
  WdsIcon,
  WdsIconNameEnum,
  WdsAvatar,
} from '@worksome/wds-components'
import { computed } from 'vue'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import PageHeader from '@/components/PageHeader.vue'
import { getAvatarPath } from '@/utils/avatar'
import AccountType from '@/utils/AccountType'
import CompanyDashboard from '@/components/Dashboard/Types/CompanyDashboard.vue'
import FreelancerDashboard from '@/components/Dashboard/Types/FreelancerDashboard.vue'
import StaffingAgencyDashboard from '@/components/Dashboard/Types/StaffingAgencyDashboard.vue'

const isCompany = computed(() => AccountType.isCompany(currentAccountType))
const isFreelancer = computed(() => AccountType.isFreelancer(currentAccountType))
const isRecruiter = computed(() => AccountType.isRecruiter(currentAccountType))

const shouldShowNewDashboard = computed(() => isCompany.value || isFreelancer.value || isRecruiter.value)

const avatarPath = computed(() => {
  const pic: string | undefined = currentUser.picture
  return getAvatarPath(pic, currentUser.globalId)
})

const userName = computed(() => currentUser.name)
const roleName = computed(() => currentTeamRoleName)
const accountName = computed(() => currentAccount.name)
const accountIcon = computed((): WdsIconNameEnum => {
  if (AccountType.isWorker(currentAccountType)) {
    return WdsIconNameEnum.USER_PROFILE_ALT
  }
  if (AccountType.isRecruiter(currentAccountType)) {
    return WdsIconNameEnum.USER_PROFILE_ALT
  }
  return WdsIconNameEnum.BUILDING
})
</script>

<style lang="scss" scoped>

/* Card styling */
.dashboard-card {
  background-color: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 1rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

  .card-title {
    font-size: 1.25rem;
    margin-bottom: 0.75rem;
  }

  .card-metric {
    font-size: 1rem;
    margin-bottom: 0.5rem;
  }

  .card-footer {
    margin-top: 0.75rem;
    color: #888888;
    font-size: 0.875rem;
  }
}
</style>
